import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Cloud, Loader2, Save, Settings, Trash2, Upload } from 'lucide-react'

import { deleteProduction, getProduction, getProductionStorage, migrateProductionStorage, patchProduction, patchProductionStorage, previewProductionStorageMigrate, type Production, type ProductionStorageView } from '@/api/studio-production'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { CatalogListPageShell } from '@/shared/components/catalog/CatalogListPageShell'
import { PageHeading } from '@/shared/components/PageHeading'
import { PROJECT_TYPES } from '@/features/studio/lib/production-constants'
import {
  customStorageConfigFromRecord,
  customStorageConfigToPayload,
  validateCustomStorageConfig,
  type CustomStorageConfig,
} from '@/features/studio/lib/custom-storage-config'
import { productionHubPath } from '@/features/studio/lib/production-path'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { CustomStorageConfigForm } from '@/features/studio/components/CustomStorageConfigForm'
import {
  ProtoBtn,
  ProtoCard,
  ProtoInput,
  ProtoLabel,
  ProtoNumberInput,
  ProtoSectionTitle,
  ProtoSelect,
  ProtoTextarea,
} from '@/features/studio/components/ui-kit'

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export function SettingsPage() {
  const { productionId = '' } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [row, setRow] = useState<Production | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [storage, setStorage] = useState<ProductionStorageView | null>(null)
  const [storageLoading, setStorageLoading] = useState(true)
  const [storageSaving, setStorageSaving] = useState(false)
  const [migrating, setMigrating] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [migrateConfirm, setMigrateConfirm] = useState<{
    from_mode: string
    to_mode: string
    asset_count: number
    copiable_assets: number
    used_bytes: number
    target_mode: string
  } | null>(null)
  const [customConfig, setCustomConfig] = useState<CustomStorageConfig>(() => customStorageConfigFromRecord())

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      try {
        const data = await getProduction(productionId)
        if (!cancelled) setRow(data)
      } catch (e) {
        if (!cancelled) setError(resolveApiErrorMessage(e, 'settings.loadFailed'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productionId])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setStorageLoading(true)
      try {
        const data = await getProductionStorage(productionId)
        if (!cancelled) {
          setStorage(data)
          setCustomConfig(customStorageConfigFromRecord(data.storage_config as Record<string, string> | undefined))
        }
      } catch (e) {
        if (!cancelled) setError(resolveApiErrorMessage(e, 'settings.storageLoadFailed'))
      } finally {
        if (!cancelled) setStorageLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productionId])

  async function handleSave() {
    if (!row) return
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const updated = await patchProduction(productionId, {
        title: row.title,
        description: row.description,
        aspect_ratio: row.aspect_ratio,
        episode_target: row.episode_target,
        episodes_per_season: row.episodes_per_season ?? 0,
        project_type: row.project_type,
        default_locale: row.default_locale,
      })
      setRow(updated)
      setMessage(t('settings.saved'))
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'settings.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveCustomStorage() {
    const validationKey = validateCustomStorageConfig(customConfig, { secretOptional: true })
    if (validationKey) {
      setError(t(validationKey))
      return
    }
    setStorageSaving(true)
    setError(null)
    setMessage(null)
    try {
      const updated = await patchProductionStorage(productionId, {
        storage_config: customStorageConfigToPayload(customConfig, { omitEmptySecret: true }),
      })
      setStorage(updated)
      setCustomConfig(customStorageConfigFromRecord(updated.storage_config as Record<string, string> | undefined))
      setMessage(t('settings.storageSaved'))
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'settings.storageSaveFailed'))
    } finally {
      setStorageSaving(false)
    }
  }

  async function startMigrateStorage() {
    const target = storage?.migrate_targets?.[0]
    if (!target) return
    setError(null)
    setMessage(null)
    try {
      const preview = await previewProductionStorageMigrate(productionId, target)
      setMigrateConfirm({
        from_mode: preview.from_mode,
        to_mode: preview.to_mode,
        asset_count: preview.asset_count,
        copiable_assets: preview.copiable_assets,
        used_bytes: preview.used_bytes,
        target_mode: target,
      })
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'settings.migrateFailed'))
    }
  }

  async function confirmMigrateStorage() {
    if (!migrateConfirm) return
    setMigrating(true)
    setError(null)
    setMessage(null)
    try {
      const result = await migrateProductionStorage(productionId, {
        target_mode: migrateConfirm.target_mode,
      })
      const refreshed = await getProductionStorage(productionId)
      setStorage(refreshed)
      setRow((prev) => (prev ? { ...prev, storage_mode: result.storage_mode } : prev))
      setMigrateConfirm(null)
      setMessage(
        t('settings.migrateDone', {
          migrated: result.migrated_assets,
          skipped: result.skipped_assets,
        }),
      )
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'settings.migrateFailed'))
    } finally {
      setMigrating(false)
    }
  }

  async function confirmDelete() {
    setDeleting(true)
    try {
      await deleteProduction(productionId)
      navigate(productionHubPath())
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'hub.deleteFailed'))
    } finally {
      setDeleting(false)
      setDeleteOpen(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    )
  }

  if (!row) {
    return (
      <CatalogListPageShell>
        <p className="text-sm text-rose-300">{error ?? t('settings.loadFailed')}</p>
      </CatalogListPageShell>
    )
  }

  return (
    <CatalogListPageShell>
      <PageHeading icon={Settings} tone="indigo" title={t('settings.title')} subtitle={t('settings.subtitle')} />

      {error ? <p className="mb-4 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      {message ? <p className="mb-4 text-sm text-emerald-300">{message}</p> : null}

      <ProtoCard className="mb-4" production>
        <ProtoSectionTitle>{t('settings.sectionBasic')}</ProtoSectionTitle>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div>
              <ProtoLabel required>{t('createModal.fieldTitle')}</ProtoLabel>
              <ProtoInput value={row.title} onChange={(e) => setRow({ ...row, title: e.target.value })} />
            </div>
            <div>
              <ProtoLabel>{t('createModal.fieldDescription')}</ProtoLabel>
              <ProtoTextarea
                rows={2}
                value={row.description}
                onChange={(e) => setRow({ ...row, description: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <div>
              <ProtoLabel>{t('createModal.fieldType')}</ProtoLabel>
              <ProtoSelect
                value={row.project_type ?? 'comic_drama'}
                onChange={(e) => setRow({ ...row, project_type: e.target.value })}
              >
                {PROJECT_TYPES.map((k) => (
                  <option key={k} value={k}>
                    {t(`production.projectType.${k}`, { defaultValue: k })}
                  </option>
                ))}
              </ProtoSelect>
            </div>
            <div>
              <ProtoLabel>{t('createModal.fieldLocale')}</ProtoLabel>
              <ProtoSelect
                value={row.default_locale ?? 'zh-CN'}
                onChange={(e) => setRow({ ...row, default_locale: e.target.value })}
              >
                <option value="zh-CN">简体中文</option>
                <option value="en-US">English</option>
              </ProtoSelect>
            </div>
            <div>
              <ProtoLabel>{t('createModal.fieldAspect')}</ProtoLabel>
              <ProtoSelect
                value={row.aspect_ratio}
                onChange={(e) => setRow({ ...row, aspect_ratio: e.target.value })}
              >
                <option value="9:16">9:16</option>
                <option value="16:9">16:9</option>
                <option value="1:1">1:1</option>
                <option value="2.39:1">2.39:1</option>
              </ProtoSelect>
            </div>
            <div>
              <ProtoLabel>{t('createModal.fieldEpisodes')}</ProtoLabel>
              <ProtoNumberInput
                min={1}
                value={row.episode_target}
                onValueChange={(n) => setRow({ ...row, episode_target: n })}
              />
            </div>
            <div>
              <ProtoLabel>{t('createModal.fieldEpisodesPerSeason')}</ProtoLabel>
              <ProtoNumberInput
                min={0}
                value={row.episodes_per_season ?? 0}
                onValueChange={(n) => setRow({ ...row, episodes_per_season: n })}
              />
              <p className="mt-1 text-[11px] text-zinc-500">{t('createModal.fieldEpisodesPerSeasonHint')}</p>
            </div>
          </div>
          <div className="flex justify-end pt-1">
            <ProtoBtn size="sm" onClick={() => void handleSave()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {t('settings.save')}
            </ProtoBtn>
          </div>
        </div>
      </ProtoCard>

      <ProtoCard className="mb-4" production>
        <ProtoSectionTitle>{t('settings.sectionStorage')}</ProtoSectionTitle>
        {storageLoading ? (
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('hub.loading')}
          </div>
        ) : storage ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="flex min-w-0 items-center gap-2 text-sm text-zinc-300">
                <Cloud className="h-4 w-4 shrink-0 text-sky-400" />
                <span className="truncate">
                  {t(`settings.storageMode.${storage.storage_mode}`, {
                    defaultValue: storage.storage_mode,
                  })}
                </span>
              </div>
              <p className="min-w-0 truncate font-mono text-xs leading-5 text-zinc-500">
                {storage.storage_prefix}
              </p>
              <div className="text-xs text-zinc-400">
                <span className="text-zinc-500">{t('settings.storageAssets')}</span>{' '}
                {storage.asset_count}
              </div>
              <div className="text-xs text-zinc-400">
                <span className="text-zinc-500">{t('settings.storageUsed')}</span>{' '}
                {formatBytes(storage.used_bytes)}
              </div>
            </div>
            {row.is_owner && storage.storage_mode === 'custom' ? (
              <div className="space-y-3 rounded-xl border border-zinc-700/50 p-4">
                <p className="text-xs text-zinc-500">{t('settings.customStorageHint')}</p>
                <CustomStorageConfigForm
                  value={customConfig}
                  onChange={setCustomConfig}
                  secretOptional
                />
                <div className="flex justify-end">
                  <ProtoBtn size="sm" onClick={() => void handleSaveCustomStorage()} disabled={storageSaving}>
                    {storageSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {t('settings.storageSave')}
                  </ProtoBtn>
                </div>
              </div>
            ) : null}
            {row.is_owner && storage.can_migrate ? (
              <div className="flex justify-end pt-1">
                <ProtoBtn size="sm" variant="secondary" disabled={migrating} onClick={() => void startMigrateStorage()}>
                  {migrating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {t('settings.migrateToTenantOss')}
                </ProtoBtn>
              </div>
            ) : null}
          </div>
        ) : null}
      </ProtoCard>

      {row?.is_owner ? (
      <ProtoCard className="border-rose-500/30 !ring-rose-500/35" production>
        <ProtoSectionTitle>{t('settings.sectionDanger')}</ProtoSectionTitle>
        <p className="mb-3 text-xs text-zinc-500">{t('settings.deleteHint')}</p>
        <ProtoBtn size="sm" variant="danger" disabled={deleting} onClick={() => setDeleteOpen(true)}>
          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t('settings.delete')}
        </ProtoBtn>
      </ProtoCard>
      ) : null}

      <ConfirmDialog
        open={deleteOpen}
        title={t('settings.delete')}
        message={t('hub.deleteConfirm')}
        icon={Trash2}
        destructive
        busy={deleting}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (deleting) return
          setDeleteOpen(false)
        }}
      />

      <ConfirmDialog
        open={migrateConfirm != null}
        title={t('settings.migrateToTenantOss')}
        message={
          migrateConfirm
            ? t('settings.migrateConfirm', {
                from: t(`settings.storageMode.${migrateConfirm.from_mode}`, {
                  defaultValue: migrateConfirm.from_mode,
                }),
                to: t(`settings.storageMode.${migrateConfirm.to_mode}`, {
                  defaultValue: migrateConfirm.to_mode,
                }),
                assets: migrateConfirm.asset_count,
                copiable: migrateConfirm.copiable_assets,
                bytes: formatBytes(migrateConfirm.used_bytes),
              })
            : null
        }
        busy={migrating}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmMigrateStorage()}
        onCancel={() => {
          if (migrating) return
          setMigrateConfirm(null)
        }}
      />
    </CatalogListPageShell>
  )
}
